import { useState, useEffect, useRef, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import Modal from '@/components/layout/Modal'
import { showToast } from '@/components/layout/Toast'
import { PageHeader, Badge, Pagination, RelationCell } from '@/components/ui'
import type { RelationItem } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import { IconPlus, IconSearch, IconEye, IconEdit, IconTrash, IconUsers, IconUser } from '@/components/icons/Icons'
import type { Cliente, TipoDocumentoFiscal, RolContacto, ConfigTarifaCliente, Contacto, MiembroEquipo } from '@/lib/types'
import { useAuth } from '@/hooks/useAuth'
import { ROLES_CONTACTO } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

type ContactoForm = {
  id?: number
  nombre: string
  apellido: string | null
  email: string | null
  telefono: string | null
  roles: RolContacto[]
  es_principal?: boolean
  auth_user_id?: string | null
  portal_activo?: boolean
  onboarding_completado?: boolean
  _deleted?: boolean
}

const emptyCliente: Omit<Cliente, 'id'> = {
  empresa: '', ruc_dni: '', direccion: '',
  tipo_documento_fiscal: 'RUC', razon_social: '', direccion_fiscal: '', ubigeo: '',
  pais: 'PE', es_extranjero: false, tipo_entidad: 'empresa', origen_lead: null, etapa_pipeline: null, fecha_primer_contacto: null,
}

const emptyContacto = (isFirst = false): ContactoForm => ({ nombre: '', apellido: '', email: '', telefono: '', roles: isFirst ? ['PRINCIPAL', 'FACTURACIÓN'] : [] })

export default function Clientes() {
  const navigate = useNavigate()
  // La pagina se monta en el ERP y en el espacio partner. La tarifa la fija
  // solo 1Conecta, asi que su bloque y su escritura quedan fuera del partner.
  const enPartner = useLocation().pathname.startsWith('/partner')
  const api = useApi()
  // Las fichas viven en rutas distintas segun el espacio que monte la pagina.
  const rutaEmpresas = enPartner ? '/partner/empresas' : '/clientes'
  const rutaContactos = enPartner ? '/partner/contactos' : '/clientes/contactos'
  const { data: clientes = [], isLoading: loading } = useCachedQuery<Cliente>('clientes')
  const { addMut, updateMut, removeMut, invalidate } = useCachedMutations('clientes')
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<Cliente | null>(null)
  const [editing, setEditing] = useState<Cliente | null>(null)
  const [form, setForm] = useState(emptyCliente)
  const [contactos, setContactos] = useState<ContactoForm[]>([])
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [tipoFilter, setTipoFilter] = useState<'todos' | 'empresa' | 'persona'>('todos')
  const [lookingUpRuc, setLookingUpRuc] = useState(false)
  const [rucLocked, setRucLocked] = useState(false)
  const [tarifaForm, setTarifaForm] = useState({ tarifa_hora: 0, umbral_horas_mes: 0, moneda: 'USD' })
  const [tarifaId, setTarifaId] = useState<number | null>(null)
  const [contactSearch, setContactSearch] = useState('')
  const [contactResults, setContactResults] = useState<Contacto[]>([])
  const [showContactSearch, setShowContactSearch] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  // ─── Delete dependencies state ───
  type DepRecord = { id: number; label: string; table: string }
  const [deps, setDeps] = useState<DepRecord[]>([])
  const [depsLoading, setDepsLoading] = useState(false)
  const [reassignTarget, setReassignTarget] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  // ─── Los contactos de cada empresa, para la tabla ───
  /*
    Antes se guardaba UNO por empresa, el primero que devolvia la consulta, y
    esa era la respuesta equivocada dos veces: ni era el principal (LibreOvo
    salia con Aldo teniendo a Luis Pedro como principal) ni decia que hubiera
    mas. Ahora se guardan todos, con el principal al frente.
  */
  type ContactoVinculo = {
    id: number
    nombre: string
    email: string | null
    telefono: string | null
    cargo: string | null
    roles: string[]
    es_principal: boolean
  }
  const [contactosPorCliente, setContactosPorCliente] = useState<Record<number, ContactoVinculo[]>>({})

  async function cargarVinculos() {
    try {
      const data = await api.get<any[]>('/api/contactos/with-empresas')
      const mapa: Record<number, ContactoVinculo[]> = {}
      for (const row of data) {
        const ct = row.contactos
        const vinculo = row.contacto_empresa
        const clienteId = vinculo?.cliente_id
        if (!ct || !clienteId) continue
        const lista = mapa[clienteId] ?? (mapa[clienteId] = [])
        if (lista.some(x => x.id === ct.id)) continue
        lista.push({
          id: ct.id,
          nombre: [ct.nombre, ct.apellido].filter(Boolean).join(' '),
          email: ct.email ?? null,
          telefono: ct.telefono ?? null,
          cargo: vinculo.cargo_empresa || ct.cargo || null,
          roles: vinculo.roles ?? [],
          es_principal: !!vinculo.es_principal,
        })
      }
      // El orden del backend ya trae al principal primero, pero el principal
      // es lo que decide que se lee en la tabla: se garantiza aca tambien.
      for (const lista of Object.values(mapa)) {
        lista.sort((a, b) => Number(b.es_principal) - Number(a.es_principal))
      }
      setContactosPorCliente(mapa)
    } catch (error) {
      console.error('Error cargando los contactos de las empresas:', error)
    }
  }

  useEffect(() => { cargarVinculos() }, [clientes])

  /* ─── Responsable comercial ───
   *
   * La columna existe sólo para el admin: es quien reparte la cartera. Una
   * sesión de ventas ya ve nada más lo suyo, así que una columna que dijera
   * su propio nombre en todas las filas no informaría nada, y el PUT se lo
   * rechazaría igual.
   */
  const { isAdmin } = useAuth()
  /**
   * Admin del ERP: quien reparte la cartera y fija la tarifa por hora. Ni el
   * partner ni una sesión de ventas — para esta última `/api/config/*` está
   * fuera de sus módulos, así que pedirlo devuelve 403.
   */
  const esAdminDelErp = isAdmin && !enPartner
  const puedeAsignar = esAdminDelErp
  const [equipo, setEquipo] = useState<MiembroEquipo[]>([])
  const [asignando, setAsignando] = useState<number | null>(null)

  useEffect(() => {
    if (!puedeAsignar) return
    api.get<MiembroEquipo[]>('/api/auth/equipo')
      .then(data => setEquipo(Array.isArray(data) ? data : []))
      .catch(() => { /* sin equipo la columna queda con el guion: no rompe la tabla */ })
  }, [puedeAsignar])

  const nombreDe = (m: MiembroEquipo) => m.nombre || m.email

  async function asignarResponsable(cliente: Cliente, valor: string) {
    const responsable_id = valor ? Number(valor) : null
    setAsignando(cliente.id)
    try {
      await api.put(`/api/clientes/${cliente.id}`, { responsable_id })
      invalidate()
      const quien = equipo.find(m => m.id === responsable_id)
      showToast(quien ? `${cliente.empresa} pasó a ${nombreDe(quien)}` : `${cliente.empresa} quedó sin responsable`, 'success')
    } catch {
      showToast('No se pudo cambiar el responsable', 'error')
    } finally {
      setAsignando(null)
    }
  }

  /** El contacto que representa a la empresa en la tabla. */
  const principalDe = (clienteId: number): ContactoVinculo | undefined => {
    const lista = contactosPorCliente[clienteId]
    return lista?.find(ct => ct.es_principal) ?? lista?.[0]
  }

  const itemsContacto = (c: Cliente): RelationItem[] =>
    (contactosPorCliente[c.id] ?? []).map(ct => ({
      id: ct.id,
      label: ct.nombre,
      sub: ct.email || ct.telefono || ct.cargo,
      badges: ct.roles,
      to: `${rutaContactos}/${ct.id}`,
      principal: ct.es_principal,
    }))

  async function loadContactos(clienteId: number): Promise<ContactoForm[]> {
    try {
      const data = await api.get<any[]>(`/api/contactos/cliente/${clienteId}`)
      return data.map((ce: any) => ({
        id: ce.contactos?.id,
        nombre: ce.contactos?.nombre ?? '',
        apellido: ce.contactos?.apellido ?? null,
        email: ce.contactos?.email ?? null,
        telefono: ce.contactos?.telefono ?? null,
        roles: ce.contacto_empresa?.roles ?? [],
        es_principal: ce.contacto_empresa?.es_principal ?? false,
        auth_user_id: ce.contactos?.auth_user_id ?? null,
        portal_activo: ce.contactos?.portal_activo ?? false,
        onboarding_completado: ce.contactos?.onboarding_completado ?? false,
      }))
    } catch {
      return []
    }
  }


  /** El alta vive en su propia vista por pasos; el modal queda para editar. */
  function openNew() {
    navigate(enPartner ? '/partner/empresas/nueva' : '/clientes/nueva')
  }

  async function openEdit(c: Cliente) {
    setEditing(c)
    setRucLocked(c.tipo_documento_fiscal === 'RUC' && !!c.razon_social)
    setShowContactSearch(false); setContactSearch(''); setContactResults([])
    setForm({
      empresa: c.empresa, ruc_dni: c.ruc_dni ?? '', direccion: c.direccion ?? '',
      tipo_documento_fiscal: c.tipo_documento_fiscal || 'RUC',
      razon_social: c.razon_social ?? '', direccion_fiscal: c.direccion_fiscal ?? '', ubigeo: c.ubigeo ?? '',
      pais: c.pais ?? 'PE', es_extranjero: c.es_extranjero ?? false, tipo_entidad: c.tipo_entidad ?? 'empresa',
      origen_lead: c.origen_lead ?? null, etapa_pipeline: c.etapa_pipeline ?? null,
      fecha_primer_contacto: c.fecha_primer_contacto ?? null,
    })
    const existing = await loadContactos(c.id)
    setContactos(existing.length ? existing.map(ct => ({ ...ct })) : [emptyContacto()])
    // Load tarifa config
    try {
      if (!esAdminDelErp) throw new Error('sin tarifa')
      const allTarifas = await api.get<ConfigTarifaCliente[]>('/api/config/tarifa-cliente')
      const found = allTarifas.find(t => t.cliente_id === c.id)
      if (found) {
        setTarifaForm({ tarifa_hora: found.tarifa_hora, umbral_horas_mes: found.umbral_horas_mes, moneda: found.moneda })
        setTarifaId(found.id)
      } else {
        setTarifaForm({ tarifa_hora: 0, umbral_horas_mes: 0, moneda: 'USD' })
        setTarifaId(null)
      }
    } catch { /* silently fail */ }
    setModalOpen(true)
  }

  async function lookupRuc() {
    const ruc = form.ruc_dni?.trim() ?? ''
    if (form.tipo_documento_fiscal !== 'RUC') { showToast('La búsqueda solo funciona con RUC', 'error'); return }
    if (!/^\d{11}$/.test(ruc)) { showToast('Ingresa un RUC válido de 11 dígitos', 'error'); return }
    setLookingUpRuc(true)
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'
      const res = await fetch(`${API_URL}/api/external/lookup-ruc?ruc=${ruc}`)
      const data = await res.json()
      if (!data.ok) { showToast(data.message || 'No se pudo consultar el RUC', 'error'); return }
      setForm(f => ({ ...f, razon_social: data.razon_social || f.razon_social, direccion_fiscal: data.direccion || f.direccion_fiscal, ubigeo: data.ubigeo || f.ubigeo }))
      setRucLocked(true)
      if (data.estado && data.estado !== 'ACTIVO') showToast(`Estado SUNAT: ${data.estado}`, 'error')
      else if (data.condicion && data.condicion !== 'HABIDO') showToast(`Condición SUNAT: ${data.condicion}`, 'error')
      else showToast('Datos fiscales cargados desde SUNAT', 'success')
    } catch { showToast('Error de conexión al consultar SUNAT', 'error') }
    finally { setLookingUpRuc(false) }
  }

  // ─── Contacto array helpers ───
  function addContacto() { setContactos(prev => [...prev, emptyContacto(prev.length === 0)]) }
  function removeContacto(idx: number) { setContactos(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)) }
  function updateContacto(idx: number, field: keyof ContactoForm, value: string | RolContacto[]) {
    setContactos(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c))
  }
  function toggleRole(idx: number, role: RolContacto) {
    setContactos(prev => prev.map((c, i) => {
      if (i !== idx) return c
      const has = c.roles.includes(role)
      return { ...c, roles: has ? c.roles.filter(r => r !== role) : [...c.roles, role] }
    }))
  }

  async function handleSubmit(e?: FormEvent) {
    if (e) e.preventDefault()
    if (saving) return
    if (!form.empresa.trim()) { showToast('El nombre de empresa es requerido', 'error'); return }
    if (!form.ruc_dni?.trim()) { showToast('El N° de documento fiscal es requerido', 'error'); return }
    if (form.tipo_entidad !== 'persona') {
      if (!form.razon_social?.trim()) { showToast('La razón social es requerida', 'error'); return }
      if (!form.direccion_fiscal?.trim()) { showToast('La dirección fiscal es requerida', 'error'); return }
    }
    if (form.tipo_documento_fiscal === 'RUC' && !/^\d{11}$/.test(form.ruc_dni?.trim() ?? '')) {
      showToast('El RUC debe tener exactamente 11 dígitos numéricos', 'error'); return
    }
    // Validate contacts
    const validContactos = contactos.filter(c => c.nombre.trim())
    if (validContactos.length === 0) { showToast('Agrega al menos un contacto con nombre', 'error'); return }
    for (const c of validContactos) {
      if (c.roles.length === 0) { showToast(`El contacto "${c.nombre}" necesita al menos un rol`, 'error'); return }
      if (!c.email?.trim()) { showToast(`El contacto "${c.nombre}" necesita un email`, 'error'); return }
    }

    setSaving(true)
    try {
      let clienteId: number
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, ...form })
        clienteId = editing.id
        // Sync contactos via new tables: contactos + contacto_empresa
        const existing = await loadContactos(clienteId)
        const existingIds = existing.map(c => c.id!).filter(Boolean)
        const formIds = validContactos.filter(c => c.id).map(c => c.id!)
        const toDelete = existingIds.filter(id => !formIds.includes(id))
        // Remove orphaned junction + contact records
        for (const id of toDelete) {
          await api.del(`/api/contactos/${id}/empresas?cliente_id=${clienteId}`)
          // Only delete contact if not linked to other companies
          const otherLinks = await api.get<any[]>(`/api/contactos/${id}/empresas`)
          if (!otherLinks || otherLinks.length === 0) {
            await api.del(`/api/contactos/${id}`)
          }
        }
        // Upsert contacts
        for (const ct of validContactos) {
          let contactoId = ct.id
          if (contactoId) {
            await api.put(`/api/contactos/${contactoId}`, { nombre: ct.nombre, apellido: ct.apellido || null, email: ct.email || null, telefono: ct.telefono || null })
          } else {
            const created = await api.post<any>('/api/contactos', { nombre: ct.nombre, apellido: ct.apellido || null, email: ct.email || null, telefono: ct.telefono || null })
            contactoId = created.id
          }
          // Upsert junction
          const existingJunction = await api.get<any[]>(`/api/contactos/${contactoId}/empresas?cliente_id=${clienteId}`)
          if (existingJunction && existingJunction.length > 0) {
            await api.put(`/api/contactos/junction/${existingJunction[0].id}`, { roles: ct.roles, es_principal: ct.es_principal ?? false })
          } else {
            await api.post(`/api/contactos/${contactoId}/empresas`, { cliente_id: clienteId, roles: ct.roles, es_principal: ct.es_principal ?? false })
          }
        }
        showToast('Cliente actualizado', 'success')
        // Save tarifa config
        if (esAdminDelErp && (tarifaForm.tarifa_hora > 0 || tarifaForm.umbral_horas_mes > 0)) {
          if (tarifaId) {
            await api.put(`/api/config/tarifa-cliente/${tarifaId}`, { cliente_id: clienteId, ...tarifaForm })
          } else {
            await api.post('/api/config/tarifa-cliente', { cliente_id: clienteId, ...tarifaForm })
          }
        }
      } else {
        const createdCliente = await addMut.mutateAsync(form) as any
        clienteId = createdCliente.id
        for (const ct of validContactos) {
          const created = await api.post<any>('/api/contactos', { nombre: ct.nombre, apellido: ct.apellido || null, email: ct.email || null, telefono: ct.telefono || null })
          await api.post(`/api/contactos/${created.id}/empresas`, { cliente_id: clienteId, roles: ct.roles, es_principal: ct.es_principal ?? false })
        }
        showToast('Cliente creado', 'success')
        // Save tarifa config
        if (esAdminDelErp && (tarifaForm.tarifa_hora > 0 || tarifaForm.umbral_horas_mes > 0)) {
          await api.post('/api/config/tarifa-cliente', { cliente_id: clienteId, ...tarifaForm })
        }
      }
      setModalOpen(false)
      cargarVinculos() // Refresh contact display
    } catch { showToast('Error al guardar cliente', 'error') }
    finally { setSaving(false) }
  }

  async function initiateDelete(c: Cliente) {
    setDeleteConfirm(c)
    setDepsLoading(true)
    setDeps([])
    setReassignTarget(null)
    try {
      const [cotRes, facRes, proyRes] = await Promise.all([
        api.get<any[]>(`/api/cotizaciones?cliente_id=${c.id}`),
        api.get<any[]>(`/api/facturas?cliente_id=${c.id}`),
        api.get<any[]>(`/api/proyectos?cliente_id=${c.id}`),
      ])
      const depList: DepRecord[] = []
      for (const r of cotRes ?? []) depList.push({ id: r.id, label: `Cotización ${r.numero ?? ''}`, table: 'cotizaciones' })
      for (const r of facRes ?? []) depList.push({ id: r.id, label: `Factura ${r.numero ?? ''} (${r.estado ?? ''})`, table: 'facturas' })
      for (const r of proyRes ?? []) depList.push({ id: r.id, label: `Proyecto ${r.nombre ?? ''} (${r.estado ?? ''})`, table: 'proyectos' })
      setDeps(depList)
    } catch { showToast('Error al verificar dependencias', 'error') }
    finally { setDepsLoading(false) }
  }

  async function deleteOneDep(dep: DepRecord) {
    try {
      if (dep.table === 'proyectos') {
        await api.del(`/api/tareas/by-proyecto/${dep.id}`)
      }
      await api.del(`/api/${dep.table}/${dep.id}`)
      setDeps(prev => prev.filter(d => !(d.id === dep.id && d.table === dep.table)))
      showToast(`${dep.label.split(' — ')[0]} eliminado`, 'success')
    } catch { showToast(`Error al eliminar ${dep.table}`, 'error') }
  }

  async function reassignAllDeps() {
    if (!reassignTarget || !deleteConfirm) return
    try {
      for (const dep of deps) {
        await api.put(`/api/${dep.table}/${dep.id}`, { cliente_id: reassignTarget })
      }
      setDeps([])
      showToast('Registros reasignados exitosamente', 'success')
    } catch { showToast('Error al reasignar registros', 'error') }
  }

  async function finalDelete() {
    if (!deleteConfirm) return
    setDeleting(true)
    try {
      const clienteId = deleteConfirm.id

      // 1. Get all contacto_empresa junction records for this client
      const junctions = await api.get<any[]>(`/api/contactos/cliente/${clienteId}`)

      if (junctions && junctions.length > 0) {
        const contactoIds = [...new Set(junctions.map((j: any) => j.contacto_empresa?.contacto_id).filter(Boolean))]

        // 2. Delete junction records
        for (const j of junctions) {
          if (j.contacto_empresa?.id) {
            await api.del(`/api/contactos/junction/${j.contacto_empresa.id}`)
          }
        }

        // 3. Delete orphaned contactos (not linked to other companies)
        for (const cId of contactoIds) {
          const otherLinks = await api.get<any[]>(`/api/contactos/${cId}/empresas`)
          if (!otherLinks || otherLinks.length === 0) {
            await api.del(`/api/contactos/${cId}`)
          }
        }
      }

      // 4. Clean up other child tables
      try { await api.del(`/api/config/tarifa-cliente/by-cliente/${clienteId}`) } catch { /* may not have records */ }
      try { await api.del(`/api/config/portal-accesos/by-cliente/${clienteId}`) } catch { /* may not have records */ }

      // 5. Delete the client
      await removeMut.mutateAsync(clienteId)
      showToast('Empresa eliminada', 'success')
      setDeleteConfirm(null)
      cargarVinculos()
    } catch (err) {
      console.error('Delete error:', err)
      showToast('Error al eliminar empresa', 'error')
    }
    finally { setDeleting(false) }
  }



  const filtered = clientes.filter(c => {
    // Busca en TODOS los contactos de la empresa: con uno solo, escribir el
    // nombre de alguien que esta en la lista no encontraba su empresa.
    const deLaEmpresa = contactosPorCliente[c.id] ?? []
    const campos = [c.empresa, c.ruc_dni, c.razon_social, ...deLaEmpresa.flatMap(ct => [ct.nombre, ct.email])]
    const matchesSearch = campos.some(v => v?.toLowerCase().includes(search.toLowerCase()))
    const tipo = c.tipo_entidad ?? 'empresa'
    const matchesTipo = tipoFilter === 'todos' || tipo === tipoFilter
    return matchesSearch && matchesTipo
  })

  // El corte sale del hook y no de un slice a mano: `resetKey` es la firma de
  // los filtros, para no quedar mirando la pagina 7 de una lista de 2.
  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtered, 15, `${search}|${tipoFilter}`)

  const hayFiltro = search.trim() !== '' || tipoFilter !== 'todos'

  const rolBadgeColors: Record<string, string> = {
    'PRINCIPAL': '#00d2d3', 'FACTURACIÓN': '#f39c12', 'TÉCNICO': '#3498db', 'COMERCIAL': '#2ecc71', 'GERENCIA': '#9b59b6'
  }

  // Esqueleto y no spinner: conserva el alto de la tabla, asi que cuando
  // llegan los datos nada salta de sitio.
  if (loading) return (
    <div className="page-enter page-shell">
      <PageHeader breadcrumbs={[{ label: 'Clientes' }, { label: 'Empresas' }]} />
      <div className="table-container">
        <table>
          <thead><tr><th>Empresa</th><th>RUC / DNI</th><th>Razon Social</th><th>Contacto</th><th>Email</th><th /></tr></thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 6 }).map((__, j) => (
                <td key={j}><span className="skeleton-line" /></td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  return (
    <div className="page-enter page-shell">
      <PageHeader
        breadcrumbs={[{ label: 'Clientes' }, { label: 'Empresas' }]}
        meta={`${clientes.length} registrada${clientes.length !== 1 ? 's' : ''}`}
        actions={
          <button className="btn btn-primary" onClick={openNew} id="btn-new-client">
            <IconPlus size={16} /> Nueva Empresa
          </button>
        }
      />

      {clientes.length > 0 && (
        <div className="filters-bar">
          <div className="filters-bar__search">
            <IconSearch size={14} className="filters-bar__search-icon" />
            <input
              className="form-input"
              placeholder="Buscar por empresa, contacto, email o RUC..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Segmentado y no un <select>: son tres opciones excluyentes y
              siempre visibles, asi que cuesta un clic en vez de dos. */}
          <div className="segmented" role="group" aria-label="Tipo de entidad">
            {(['todos', 'empresa', 'persona'] as const).map(t => (
              <button
                key={t}
                type="button"
                className={`segmented__opt${tipoFilter === t ? ' is-active' : ''}`}
                aria-pressed={tipoFilter === t}
                onClick={() => setTipoFilter(t)}
              >
                {t === 'todos' ? 'Todos' : t === 'empresa' ? 'Empresas' : 'Personas'}
              </button>
            ))}
          </div>

          {hayFiltro && (
            <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={() => { setSearch(''); setTipoFilter('todos') }}>
              Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* Vacio de verdad y vacio por filtro son pantallas distintas: la primera
          explica que es esto y ofrece el alta, la segunda dice que el filtro
          esta recortando y ofrece quitarlo. Con un solo mensaje el usuario no
          sabe si el sistema esta vacio o si se equivoco el. */}
      {clientes.length === 0 ? (
        <div className="empty-state">
          <IconUsers size={44} />
          <h3>Sin empresas</h3>
          <p>Crea tu primera empresa para comenzar a cotizar.</p>
          <button className="btn btn-primary" onClick={openNew}>Crear Empresa</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <IconSearch size={44} />
          <h3>Ninguna empresa coincide</h3>
          <p>
            {search.trim() ? <>No hay resultados para <strong>{search}</strong>.</> : 'El filtro de tipo no deja pasar ninguna.'}
          </p>
          <button className="btn btn-secondary" onClick={() => { setSearch(''); setTipoFilter('todos') }}>
            Limpiar filtros
          </button>
        </div>
      ) : (
        <>
          <div className="table-container page-shell__table">
            <table>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>RUC / DNI</th>
                  <th>Razon Social</th>
                  <th>Contacto</th>
                  <th>Email</th>
                  {puedeAsignar && <th>Responsable</th>}
                  <th className="col-acciones">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(c => (
                  <tr key={c.id}>
                    <td className="cell-strong">
                      {/* El nombre ES la entrada a la ficha: el ojo de la fila
                          sigue estando, pero nadie busca un boton para abrir
                          algo cuyo nombre ya esta escrito. */}
                      <Link to={`${rutaEmpresas}/${c.id}`} className="cell-enlace">{c.empresa}</Link>
                      {(c.tipo_entidad ?? 'empresa') === 'persona' && <Badge variant="info">Persona</Badge>}
                    </td>
                    <td className="cell-mono">{c.ruc_dni ?? '\u2014'}</td>
                    <td className="cell-truncate">{c.razon_social ?? '\u2014'}</td>
                    <td>
                      <RelationCell
                        items={itemsContacto(c)}
                        titulo={`Contactos de ${c.empresa}`}
                        nombre={['contacto', 'contactos']}
                        icono={<IconUser size={13} />}
                      />
                    </td>
                    <td>{principalDe(c.id)?.email ?? '\u2014'}</td>
                    {puedeAsignar && (
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={c.responsable_id ?? ''}
                          disabled={asignando === c.id}
                          onChange={e => asignarResponsable(c, e.target.value)}
                          aria-label={`Responsable de ${c.empresa}`}
                        >
                          <option value="">Sin asignar</option>
                          {equipo.map(m => (
                            <option key={m.id} value={m.id}>{nombreDe(m)}</option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td className="col-acciones">
                      {/* nowrap: con los botones en dos lineas la fila crecia al doble. */}
                      <div className="row-actions row-actions--nowrap">
                        <button className="btn btn-sm btn-secondary" onClick={() => navigate(`${rutaEmpresas}/${c.id}`)} title="Ver ficha" aria-label="Ver ficha">
                          <IconEye size={15} />
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(c)} title="Editar" aria-label="Editar">
                          <IconEdit size={15} />
                        </button>
                        {/* DELETE /clientes/:id es adminOnly; sin esto el partner
                            abriria el modal para acabar en un 403. */}
                        {!enPartner && (
                          <button className="btn btn-sm btn-danger" onClick={() => initiateDelete(c)} title="Eliminar" aria-label="Eliminar">
                            <IconTrash size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page} totalPages={totalPages} total={total} pageSize={pageSize}
            onPage={setPage} noun="empresas"
          />
        </>
      )}

      {/* ─── Create / Edit Modal ─── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar Empresa' : 'Nueva Empresa'}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={() => handleSubmit()}>{saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}</button>
          </>
        }
      >
        <form ref={formRef} onSubmit={handleSubmit}>
          {/* Section 1: Datos Fiscales */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem', marginBottom: '1rem', background: 'color-mix(in srgb, var(--rol-principal) 3%, transparent)' }}>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21h18"/><path d="M3 10h18"/><path d="M5 6l7-3 7 3"/><path d="M4 10v11"/><path d="M20 10v11"/><path d="M8 14v3"/><path d="M12 14v3"/><path d="M16 14v3"/></svg>
              Datos Fiscales (Obligatorios para SUNAT)
            </h4>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {(['empresa', 'persona'] as const).map(t => {
                const active = (form.tipo_entidad ?? 'empresa') === t
                return (
                  <button key={t} type="button" onClick={() => { setForm(f => ({ ...f, tipo_entidad: t, tipo_documento_fiscal: t === 'persona' ? 'DNI' : 'RUC', razon_social: '', direccion_fiscal: '', ubigeo: '' })); setRucLocked(false) }} style={{
                    flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                    border: active ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                    background: active ? 'color-mix(in srgb, var(--primary-light) 12%, transparent)' : 'transparent',
                    color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  }}>{t === 'empresa' ? 'Empresa' : 'Persona / Freelancer'}</button>
                )
              })}
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: '0 0 140px' }}>
                <label className="form-label">Tipo Doc. *</label>
                <select className="form-select" value={form.tipo_documento_fiscal} onChange={e => { setForm(f => ({ ...f, tipo_documento_fiscal: e.target.value as TipoDocumentoFiscal, razon_social: '', direccion_fiscal: '', ubigeo: '' })); setRucLocked(false) }}>
                  <option value="RUC">RUC</option><option value="DNI">DNI</option><option value="CE">CE</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">N° Documento *</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-input" value={form.ruc_dni ?? ''} onChange={e => setForm(f => ({ ...f, ruc_dni: e.target.value }))} placeholder={form.tipo_documento_fiscal === 'RUC' ? '20XXXXXXXXX' : '7XXXXXXXX'} maxLength={form.tipo_documento_fiscal === 'RUC' ? 11 : 15} style={{ fontFamily: 'monospace' }} required />
                  {form.tipo_documento_fiscal === 'RUC' && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={lookupRuc} disabled={lookingUpRuc} title="Buscar en SUNAT" style={{ whiteSpace: 'nowrap', minWidth: 44 }}>
                      {lookingUpRuc ? <div className="auth-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{(form.tipo_entidad ?? 'empresa') === 'persona' ? 'Razón Social (opcional)' : 'Razón Social *'}{rucLocked && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 6 }}>(SUNAT)</span>}</label>
              <input className="form-input" value={form.razon_social ?? ''} onChange={e => setForm(f => ({ ...f, razon_social: e.target.value }))} placeholder="Nombre legal ante SUNAT" required={(form.tipo_entidad ?? 'empresa') !== 'persona'} readOnly={rucLocked} style={rucLocked ? { opacity: 0.7, cursor: 'not-allowed' } : undefined} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{(form.tipo_entidad ?? 'empresa') === 'persona' ? 'Dirección Fiscal (opcional)' : 'Dirección Fiscal *'}{rucLocked && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 6 }}>(SUNAT)</span>}</label>
                <input className="form-input" value={form.direccion_fiscal ?? ''} onChange={e => setForm(f => ({ ...f, direccion_fiscal: e.target.value }))} placeholder="Domicilio legal registrado" required={(form.tipo_entidad ?? 'empresa') !== 'persona'} readOnly={rucLocked} style={rucLocked ? { opacity: 0.7, cursor: 'not-allowed' } : undefined} />
              </div>
              <div className="form-group" style={{ flex: '0 0 140px' }}>
                <label className="form-label">Ubigeo{rucLocked && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 6 }}>(SUNAT)</span>}</label>
                <input className="form-input" value={form.ubigeo ?? ''} onChange={e => setForm(f => ({ ...f, ubigeo: e.target.value }))} placeholder="150101" maxLength={6} style={rucLocked ? { fontFamily: 'monospace', opacity: 0.7, cursor: 'not-allowed' } : { fontFamily: 'monospace' }} readOnly={rucLocked} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{(form.tipo_entidad ?? 'empresa') === 'persona' ? 'Nombre completo *' : 'Nombre Comercial / Empresa *'}</label>
              <input className="form-input" value={form.empresa} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))} required />
            </div>
          </div>

          {/* Section 2: Directorio de Contactos (Dynamic Array) */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h4 style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                Directorio de Contactos ({contactos.length})
              </h4>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowContactSearch(!showContactSearch)} style={{ fontSize: '0.8rem' }}>Buscar existente</button>
                <button type="button" className="btn btn-sm btn-secondary" onClick={addContacto} style={{ fontSize: '0.8rem' }}>+ Nuevo</button>
              </div>
            </div>

            {/* Contact search */}
            {showContactSearch && (
              <div style={{ marginBottom: '0.75rem', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: 6, background: 'color-mix(in srgb, var(--info) 5%, transparent)' }}>
                <input
                  className="form-input"
                  placeholder="Buscar contacto por nombre o email..."
                  value={contactSearch}
                  onChange={async e => {
                    const q = e.target.value
                    setContactSearch(q)
                    if (q.trim().length < 2) { setContactResults([]); return }
                    try {
                      const data = await api.get<Contacto[]>(`/api/contactos?search=${encodeURIComponent(q)}`)
                      setContactResults(data)
                    } catch { setContactResults([]) }
                  }}
                  style={{ marginBottom: 6 }}
                />
                {contactResults.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {contactResults.map(cr => {
                      const alreadyAdded = contactos.some(c => c.id === cr.id)
                      return (
                        <button
                          key={cr.id}
                          type="button"
                          disabled={alreadyAdded}
                          onClick={() => {
                            setContactos(prev => [...prev, {
                              id: cr.id, nombre: cr.nombre, apellido: cr.apellido ?? '',
                              email: cr.email ?? '', telefono: cr.telefono ?? '',
                              roles: prev.length === 0 ? ['PRINCIPAL', 'FACTURACIÓN'] : [],
                            }])
                            setContactSearch(''); setContactResults([]); setShowContactSearch(false)
                          }}
                          style={{ textAlign: 'left', padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border)', background: alreadyAdded ? 'var(--bg-card-hover)' : 'var(--bg-card)', cursor: alreadyAdded ? 'not-allowed' : 'pointer', fontSize: '0.85rem', color: alreadyAdded ? 'var(--text-muted)' : 'var(--text-primary)' }}
                        >
                          <strong>{cr.nombre} {cr.apellido ?? ''}</strong>
                          {cr.email && <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>{cr.email}</span>}
                          {alreadyAdded && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>(ya agregado)</span>}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {contactos.map((ct, idx) => (
              <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '0.75rem', marginBottom: idx < contactos.length - 1 ? '0.75rem' : 0, background: 'var(--bg-secondary)', position: 'relative' }}>
                {contactos.length > 1 && (
                  <button type="button" onClick={() => removeContacto(idx)} style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }} title="Eliminar contacto">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  </button>
                )}
                <div className="form-row" style={{ marginBottom: 8 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Nombre *</label>
                    <input className="form-input" value={ct.nombre} onChange={e => updateContacto(idx, 'nombre', e.target.value)} placeholder="Nombre" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Apellido</label>
                    <input className="form-input" value={ct.apellido ?? ''} onChange={e => updateContacto(idx, 'apellido', e.target.value)} placeholder="Apellido" />
                  </div>
                </div>
                <div className="form-row" style={{ marginBottom: 8 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Email *</label>
                    <input className="form-input" type="email" value={ct.email ?? ''} onChange={e => updateContacto(idx, 'email', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Teléfono</label>
                    <input className="form-input" value={ct.telefono ?? ''} onChange={e => updateContacto(idx, 'telefono', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.78rem' }}>Roles *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ROLES_CONTACTO.map(role => {
                      const active = ct.roles.includes(role)
                      return (
                        <button key={role} type="button" onClick={() => toggleRole(idx, role)} style={{
                          padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: '1px solid',
                          background: active ? rolBadgeColors[role] : 'transparent',
                          color: active ? 'var(--text-on-primary)' : 'var(--text-secondary)',
                          borderColor: active ? rolBadgeColors[role] : 'var(--border)',
                          transition: 'all 0.15s ease',
                        }}>{role}</button>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Section 3: Configuración de Proyecto — solo 1Conecta fija tarifas */}
          {esAdminDelErp && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem', marginBottom: '1rem', background: 'color-mix(in srgb, var(--rol-principal) 3%, transparent)' }}>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
              Configuración de Tarifa (Proyectos)
            </h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Tarifa por hora</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={tarifaForm.tarifa_hora}
                  onChange={e => setTarifaForm(f => ({ ...f, tarifa_hora: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Umbral horas/mes</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.5"
                  min="0"
                  value={tarifaForm.umbral_horas_mes}
                  onChange={e => setTarifaForm(f => ({ ...f, umbral_horas_mes: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
              <div className="form-group" style={{ flex: '0 0 100px' }}>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Moneda</label>
                <select
                  className="form-select"
                  value={tarifaForm.moneda}
                  onChange={e => setTarifaForm(f => ({ ...f, moneda: e.target.value }))}
                >
                  <option value="USD">USD</option>
                  <option value="PEN">PEN</option>
                </select>
              </div>
            </div>
            {tarifaForm.tarifa_hora > 0 && (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', padding: '4px 8px', background: 'var(--bg-tertiary)', borderRadius: 6 }}>
                Ej: 10 horas → {formatCurrency(tarifaForm.tarifa_hora * 10, tarifaForm.moneda)}
                {tarifaForm.umbral_horas_mes > 0 && <> · Umbral: {tarifaForm.umbral_horas_mes}h/mes</>}
              </div>
            )}
          </div>
          )}

        </form>
      </Modal>



      {/* Delete / Dependency Management Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={`Eliminar: ${deleteConfirm?.empresa ?? ''}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
            <button className="btn btn-danger" onClick={finalDelete} disabled={deps.length > 0 || deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar empresa'}
            </button>
          </>
        }
      >
        {depsLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
            <p style={{ color: 'var(--text-secondary)' }}>Verificando registros vinculados...</p>
          </div>
        ) : deps.length === 0 ? (
          <div>
            <p style={{ marginBottom: 8 }}>¿Estás seguro de eliminar <strong>{deleteConfirm?.empresa}</strong>?</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Se eliminarán también todos sus contactos asociados. Esta acción es irreversible.</p>
          </div>
        ) : (
          <div>
            <div style={{ padding: '10px 14px', background: 'color-mix(in srgb, var(--danger) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)', borderRadius: 6, marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--danger)' }}>
              Esta empresa tiene <strong>{deps.length}</strong> registro(s) vinculado(s). Debes eliminarlos o reasignarlos antes de poder eliminar la empresa.
            </div>

            {/* Dependency list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem' }}>
              {deps.map(dep => (
                <div key={`${dep.table}-${dep.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.85rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, background: dep.table === 'cotizaciones' ? 'color-mix(in srgb, var(--info) 15%, transparent)' : dep.table === 'facturas' ? 'rgba(243,156,18,0.15)' : 'rgba(46,204,113,0.15)', color: dep.table === 'cotizaciones' ? 'var(--info)' : dep.table === 'facturas' ? 'var(--warning)' : 'var(--success)' }}>
                      {dep.table === 'cotizaciones' ? 'COT' : dep.table === 'facturas' ? 'FAC' : 'PROY'}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dep.label}</span>
                  </div>
                  <button className="btn btn-sm btn-danger" onClick={() => deleteOneDep(dep)} style={{ flexShrink: 0, marginLeft: 8, fontSize: '0.75rem', padding: '3px 8px' }}>
                    Eliminar
                  </button>
                </div>
              ))}
            </div>

            {/* Reassign option */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem', background: 'color-mix(in srgb, var(--rol-principal) 3%, transparent)' }}>
              <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: 6, display: 'block' }}>O reasignar todos a otra empresa:</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select className="form-select" value={reassignTarget ?? ''} onChange={e => setReassignTarget(e.target.value ? Number(e.target.value) : null)} style={{ flex: 1 }}>
                  <option value="">Seleccionar empresa...</option>
                  {clientes.filter(c => c.id !== deleteConfirm?.id).map(c => (
                    <option key={c.id} value={c.id}>{c.empresa}</option>
                  ))}
                </select>
                <button className="btn btn-sm btn-primary" disabled={!reassignTarget} onClick={reassignAllDeps} style={{ whiteSpace: 'nowrap' }}>Reasignar todo</button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
